import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ContentCard from '@/components/feature/ContentCard';
import { useSeo } from '@/hooks/useSeo';
import { allContent } from '@/lib/contentService';

const hotKeywords = ['星际回声', '悬疑', '陈叙白', '长安灯下', '金穗奖', '科幻', '影评'];

const typeTabs = [
  { label: '全部结果', value: 'all' },
  { label: '电影', value: 'movie' },
  { label: '电视剧', value: 'tvshow' },
  { label: '资讯', value: 'news' },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [keyword, setKeyword] = useState(query);
  const [activeType, setActiveType] = useState('all');

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useSeo({
    title: query ? `${query} - 搜索结果 - 玩偶姐姐` : '站内搜索 - 玩偶姐姐影视内容平台',
    description: query
      ? `在玩偶姐姐影视内容平台搜索「${query}」的相关电影、电视剧与影视资讯结果。`
      : '搜索电影、电视剧与影视资讯，输入片名、演员或关键词即可找到对应内容。',
    keywords: '站内搜索,影视搜索,电影搜索,电视剧搜索,玩偶姐姐搜索',
  });

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allContent.filter((item) => {
      const haystack = [
        item.title,
        item.subtitle ?? '',
        item.summary,
        item.director ?? '',
        item.author ?? '',
        item.articleCategory ?? '',
        ...item.genre,
        ...(item.cast ?? []),
        ...(item.content ?? []),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const filtered = results.filter((item) => activeType === 'all' || item.type === activeType);
  const recommendations = useMemo(
    () => [...allContent].filter((item) => item.type !== 'news').sort((a, b) => (b.heat ?? 0) - (a.heat ?? 0)).slice(0, 5),
    [],
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      setParams({});
      return;
    }
    setParams({ q: value });
  };

  const searchHot = (value: string) => {
    setKeyword(value);
    setParams({ q: value });
  };

  const counts = {
    all: results.length,
    movie: results.filter((item) => item.type === 'movie').length,
    tvshow: results.filter((item) => item.type === 'tvshow').length,
    news: results.filter((item) => item.type === 'news').length,
  } as Record<string, number>;

  const workResults = filtered.filter((item) => item.type !== 'news');
  const newsResults = filtered.filter((item) => item.type === 'news');

  const renderNewsRows = (list: typeof filtered) => (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
      {list.map((item, index) => (
        <Link
          key={item.id}
          to={`/detail/${item.id}`}
          className={`group flex cursor-pointer flex-col gap-3 p-4 transition-colors hover:bg-background-100 sm:flex-row sm:items-center md:p-5 ${
            index !== list.length - 1 ? 'border-b border-background-200' : ''
          }`}
        >
          <div className="h-[130px] w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-[92px] sm:w-[150px]">
            <img
              src={item.cover}
              alt={`${item.title} 配图`}
              title={item.title}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 font-label text-[10px] text-secondary-800">
                {item.articleCategory}
              </span>
              <span className="font-label text-[11px] text-foreground-400">{item.date}</span>
            </div>
            <h3 className="mt-1.5 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
              {item.title}
            </h3>
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">{item.summary}</p>
          </div>
        </Link>
      ))}
    </div>
  );

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-8 md:px-8 md:py-10">
          <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
            Search
          </p>
          <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[32px]">
            {query ? `搜索「${query}」` : '站内搜索'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {query
              ? `共找到 ${results.length} 条相关内容，可按类型进一步筛选。`
              : '输入片名、演员、类型或关键词，找到对应的电影、剧集与资讯。'}
          </p>

          <form onSubmit={submit} className="mt-5 flex w-full max-w-2xl flex-col gap-2 sm:flex-row">
            <div className="flex flex-1 items-center rounded-md border border-background-300 bg-background-50 pl-3.5">
              <i className="ri-search-line text-[16px] text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="例如：星际回声、悬疑、陈叙白"
                aria-label="搜索关键词"
                className="w-full bg-transparent px-2.5 py-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword('')}
                  aria-label="清空关键词"
                  className="mr-2 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-foreground-400 hover:bg-background-100"
                >
                  <i className="ri-close-line text-[14px]"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 font-label text-[11px] text-foreground-500">
              <i className="ri-fire-line text-[12px] text-primary-500"></i>
              热门搜索
            </span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => searchHot(word)}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 font-label text-[11px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-7 md:px-8 md:py-9">
        {query ? (
          <>
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-background-200 bg-background-50 p-3">
              {typeTabs.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveType(tab.value)}
                  className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    activeType === tab.value
                      ? 'bg-primary-500 font-medium text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`font-label text-[10px] ${
                      activeType === tab.value ? 'text-background-100' : 'text-foreground-400'
                    }`}
                  >
                    {counts[tab.value]}
                  </span>
                </button>
              ))}
            </div>

            {filtered.length > 0 ? (
              <div className="mt-6 space-y-8">
                {workResults.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
                    {workResults.map((item) => (
                      <ContentCard key={item.id} item={item} />
                    ))}
                  </div>
                )}

                {newsResults.length > 0 && (
                  <div>
                    {workResults.length > 0 && (
                      <h2 className="mb-3 font-heading text-sm font-semibold text-foreground-900">相关资讯</h2>
                    )}
                    {renderNewsRows(newsResults)}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-eye-line text-[26px]"></i>
                </span>
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-900">
                  没有找到与「{query}」相关的内容
                </h2>
                <p className="mt-2 text-center text-sm text-foreground-500">
                  可以试试更短的关键词，或从下方推荐内容里找找看。
                </p>
                <Link
                  to="/movies"
                  className="mt-5 cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览全部电影
                </Link>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-search-line text-[26px]"></i>
            </span>
            <h2 className="mt-4 font-heading text-base font-semibold text-foreground-900">输入关键词开始搜索</h2>
            <p className="mt-2 text-center text-sm text-foreground-500">
              支持片名、演员、导演、类型与资讯标题的全文检索。
            </p>
          </div>
        )}

        <section className="mt-10 border-t border-background-200 pt-7">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold text-foreground-950">编辑推荐</h2>
            <Link to="/movies" className="flex cursor-pointer items-center gap-1 font-label text-xs text-primary-600">
              全部电影
              <i className="ri-arrow-right-line text-[12px]"></i>
            </Link>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {recommendations.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      </section>
    </div>
  );
}